import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { roundResult } from '../calculations'
import { InvoiceLine, InvoiceLinePrice } from '../type'

import { handleLineCalculation } from './lineCalculations'
import { DeleteButton, LineTd, TdInput } from './style'
type Props = {
  lineState: InvoiceLine
  onUpdate: (updatedLine: InvoiceLine) => void
  onDelete: () => void
}

type Errors = {
  [index: string]: boolean
  name: boolean
  amount: boolean
  unitPrice: boolean
  discount: boolean
  vat: boolean
}

export const Line = ({ lineState, ...props }: Props) => {
  const [line, setLine] = useState<InvoiceLine>(lineState)
  const [errors, setErrors] = useState<Errors>({
    name: false,
    amount: false,
    unitPrice: false,
    discount: false,
    vat: false,
  })
  const { t } = useTranslation()

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.currentTarget.value
    const newLine: InvoiceLine = { ...line, name: inputVal }
    setLine(newLine)
    validate('name', inputVal)
  }

  const validate = (property: string, value: string | number) => {
    let hasError: boolean = false
    const numberCheck =
      ['amount', 'unitPrice', 'discount', 'vat', 'unitPriceInclVat'].indexOf(
        property,
      ) !== -1
        ? Number(value)
        : 0
    if ((property === 'discount' || property === 'vat') && numberCheck < 0) {
      hasError = true
    }

    if (
      (property === 'amount' ||
        property === 'unitPrice' ||
        property === 'unitPriceInclVat') &&
      numberCheck <= 0
    ) {
      hasError = true
    }

    if (property === 'name' && !value) {
      hasError = true
    }

    if (property === 'discount' && (numberCheck > 100 || numberCheck < 0)) {
      hasError = true
    }

    const newErrors = { ...errors }
    newErrors[property] = hasError
    setErrors(newErrors)
  }

  const handleDeleteLine = () => {
    props.onDelete()
  }

  const onBlur = (property: keyof InvoiceLine) => {
    validate(property, line[property])
    props.onUpdate(line)
  }

  const handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.value === '0') {
      e.currentTarget.value = ''
    }
  }

  const handleLinTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = Number(e.currentTarget.value)
    const targetName = e.currentTarget.name as keyof InvoiceLinePrice

    const newLine = handleLineCalculation({
      line,
      targetName,
      targetValue,
    })

    setLine(newLine)
    validate(targetName, roundResult(targetValue))
  }

  return (
    <tr>
      <LineTd error={errors.name}>
        <TdInput
          type="text"
          error={errors.name}
          placeholder={t('Name')}
          onChange={handleNameChange}
          value={line.name}
          onBlur={() => onBlur('name')}
        />
      </LineTd>
      <LineTd error={errors.amount} numberCell>
        <TdInput
          type="number"
          name="amount"
          error={errors.amount}
          onChange={handleLinTotalChange}
          value={line.amount ?? 0}
          onBlur={() => onBlur('amount')}
          step={1}
          min={0}
        />
      </LineTd>
      <LineTd error={errors.unitPrice} numberCell>
        <TdInput
          type="number"
          name="unitPrice"
          error={errors.unitPrice}
          onChange={handleLinTotalChange}
          onFocus={handleFocus}
          value={line.unitPrice ?? 0}
          onBlur={() => onBlur('unitPrice')}
          step={0.01}
          min={0}
        />
      </LineTd>

      <LineTd error={errors.discount} numberCell>
        <TdInput
          type="number"
          name="discount"
          error={errors.discount}
          onChange={handleLinTotalChange}
          onFocus={handleFocus}
          value={line.discount ?? 0}
          onBlur={() => onBlur('discount')}
          step={0.01}
          min={0}
          max={100}
        />
      </LineTd>

      <LineTd error={errors.vat} numberCell>
        <TdInput
          type="number"
          name="vat"
          error={errors.vat}
          onChange={handleLinTotalChange}
          value={line.vat ?? 0}
          onBlur={() => onBlur('vat')}
          min={0}
        />
      </LineTd>

      <LineTd numberCell>
        <TdInput
          type="number"
          name="unitPriceInclVat"
          error={errors.unitPriceInclVat}
          onChange={handleLinTotalChange}
          onFocus={handleFocus}
          value={line.unitPriceInclVat ?? 0}
          onBlur={() => onBlur('unitPriceInclVat')}
          step={0.01}
          min={0}
        />
      </LineTd>

      <LineTd numberCell>
        <TdInput
          type="number"
          name="lineTotal"
          value={line.lineTotal ?? 0}
          readOnly
          min={0}
          style={{ pointerEvents: 'none' }}
        />
      </LineTd>

      <td>
        <DeleteButton onClick={handleDeleteLine}>
          <FontAwesomeIcon icon={faTrashAlt as IconProp} />
        </DeleteButton>
      </td>
    </tr>
  )
}
